'use client';

import { useCallback, useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Briefcase, Building2, ChevronDown, MessageCircle, RotateCcw, Sparkles, Search, Users } from 'lucide-react';
import { ContactEntity, type ContactEntityData } from '@/components/ui/design-system/entities/contact';
import { ConnectedOutreachDialog } from '@/app/dashboard/components/outreach/ConnectedOutreachDialog';
import { ContactQuickViewDialog } from '@/app/dashboard/components/contact/ContactQuickViewDialog';
import BranchOutCompaniesTabs from '@/app/brief/components/BranchOutCompaniesTabs';
import { cn } from '@/lib/utils';
import { AnswerCard } from './AnswerCard';
import { FeedInsight } from './FeedInsight';
import { LazyCompaniesList } from './LazyCompaniesList';
import { CompanyContactsFlat } from './CompanyContactsFlat';
import { JobWithContactsCard } from './JobWithContacts';
import { ShowMoreButton } from './ShowMoreButton';
import { EntityFilterBar } from './EntityFilterBar';
import { QueryMessage } from './QueryMessage';
import { useAsyncList } from '../hooks/useAsyncList';
import type { Contact } from '@/types/report-types';
import type {
  FeedItem,
  JobWithContacts,
  ResultFilter,
  SearchLogEntry,
  SearchPagers,
  SearchRound,
  StageKey,
  StageState,
} from '../types';

interface ResultsFeedProps {
  rounds: SearchRound[];
  onEditQuery?: (query: string) => void;
  onDeleteRound?: (roundId: string) => void;
  onNewSearch?: () => void;
  pagers: SearchPagers;
  resultFilter?: ResultFilter;
  onResultFilterChange?: (filter: ResultFilter) => void;
  isLoading?: boolean;
  className?: string;
  bottomSlot?: React.ReactNode;
}

const STAGE_ICONS: Record<StageKey, React.ComponentType<{ className?: string }>> = {
  understanding: Sparkles,
  exactMatch: Search,
  similarExpansion: ArrowPathIcon,
  companies: Building2,
  jobs: Briefcase,
  expandedJobs: Briefcase,
  contacts: Users,
  answer: MessageCircle,
};

const VISIBLE_STAGES: Record<ResultFilter, Set<StageKey>> = {
  all: new Set(['understanding', 'exactMatch', 'similarExpansion', 'companies', 'jobs', 'expandedJobs', 'contacts', 'answer']),
  companies: new Set(['understanding', 'exactMatch', 'similarExpansion', 'companies', 'answer']),
  jobs: new Set(['understanding', 'jobs', 'expandedJobs', 'answer']),
  contacts: new Set(['understanding', 'companies', 'contacts', 'answer']),
};

export function ResultsFeed({
  rounds,
  onEditQuery,
  onDeleteRound,
  onNewSearch,
  pagers,
  resultFilter = 'all',
  onResultFilterChange,
  isLoading,
  className,
  bottomSlot,
}: ResultsFeedProps) {
  const allowedStages = VISIBLE_STAGES[resultFilter];
  const lastRound = rounds[rounds.length - 1];
  const isSearching = lastRound
    ? Object.values(lastRound.stages).some((s) => s === 'running')
    : false;

  return (
    <div className={cn('-mx-4 sm:mx-0', className)}>
      <div className="border border-border bg-card overflow-x-hidden" style={{ overflowAnchor: 'auto' }}>
        {(onResultFilterChange || onNewSearch) ? (
          <div className="flex items-center justify-between border-b border-border/40 px-3 py-2 sm:px-4">
            {onResultFilterChange ? (
              <EntityFilterBar value={resultFilter} onChange={onResultFilterChange} />
            ) : <div />}
            {onNewSearch && (
              <button
                type="button"
                onClick={onNewSearch}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <RotateCcw className="h-3 w-3" />
                New Search
              </button>
            )}
          </div>
        ) : null}
        <div className="divide-y divide-border/40">
          {rounds.map((round, idx) => (
            <RoundSection
              key={round.id}
              round={round}
              isLast={idx === rounds.length - 1}
              isSearching={isSearching}
              onEditQuery={onEditQuery}
              onDeleteRound={onDeleteRound ? () => onDeleteRound(round.id) : undefined}
              pagers={pagers}
              resultFilter={resultFilter}
              allowedStages={allowedStages}
            />
          ))}
          {isSearching && (
            <ActiveLoadingSection stages={lastRound.stages} />
          )}
          {isLoading && rounds.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground sm:px-4">
              <RotateCcw className="h-3.5 w-3.5 animate-spin" />
              Loading saved search…
            </div>
          )}
        </div>
        {bottomSlot && (
          <div className="border-t border-border/40">
            {bottomSlot}
          </div>
        )}
      </div>
    </div>
  );
}

function RoundSection({
  round,
  isLast,
  isSearching,
  onEditQuery,
  onDeleteRound,
  pagers,
  resultFilter,
  allowedStages,
}: {
  round: SearchRound;
  isLast: boolean;
  isSearching: boolean;
  onEditQuery?: (query: string) => void;
  onDeleteRound?: () => void;
  pagers: SearchPagers;
  resultFilter: ResultFilter;
  allowedStages: Set<StageKey>;
}) {
  const { feed, stages, logs } = round;

  const exactSection = feed.find((item) => item.type === 'exactCompanySection');
  const similarSection = feed.find((item) => item.type === 'similarCompaniesSection');
  const topMatchesSection = feed.find((item) => item.type === 'topMatchesSection');
  const jobsSection = feed.find((item) => item.type === 'jobsSection' && item.id !== 'expanded-jobs' && item.jobs.length > 0);
  const expandedJobsSection = feed.find((item) => item.type === 'jobsSection' && item.id === 'expanded-jobs' && item.jobs.length > 0);
  const contactsSection = feed.find((item) => item.type === 'contactsSection' && item.contacts.length > 0);
  const answerSection = feed.find((item) => item.type === 'answerSection');

  const exactCompanyName = exactSection?.type === 'exactCompanySection' ? exactSection.companyName : null;
  const similarCompanyNames = similarSection?.type === 'similarCompaniesSection' ? similarSection.companyNames : [];

  const stageContent: Partial<Record<StageKey, React.ReactNode>> = {};

  const allCompanyNamesDeduped = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    const candidates = [
      ...(exactCompanyName ? [exactCompanyName] : []),
      ...similarCompanyNames,
      ...((topMatchesSection?.type === 'topMatchesSection' ? topMatchesSection.matches.map((m) => m.company.name).filter(Boolean) : []) as string[]),
    ];
    for (const name of candidates) {
      const key = name.trim().toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(name);
      }
    }
    return result;
  }, [exactCompanyName, similarCompanyNames, topMatchesSection]);

  const companiesLoading = stages.exactMatch === 'running' || stages.similarExpansion === 'running' || stages.companies === 'running';

  if (allCompanyNamesDeduped.length > 0) {
    const companyStageKey: StageKey = exactCompanyName ? 'exactMatch' : similarCompanyNames.length > 0 ? 'similarExpansion' : 'companies';
    stageContent[companyStageKey] = (
      <BranchOutCompaniesTabs
        companyNames={allCompanyNamesDeduped}
        featuredCompanyName={exactCompanyName ?? undefined}
        isLoadingCompanies={companiesLoading}
        deferContactLoading={isSearching}
        embedded
      />
    );
  }

  if (jobsSection?.type === 'jobsSection') {
    stageContent.jobs = (
      <div className="px-3 py-3 sm:px-4">
        <InlineJobsList
          jobs={jobsSection.jobs}
          total={jobsSection.total}
          commentary={jobsSection.commentary}
          loadMore={pagers.loadMoreJobs}
        />
      </div>
    );
  }

  if (expandedJobsSection?.type === 'jobsSection') {
    stageContent.expandedJobs = (
      <div className="px-3 py-3 sm:px-4">
        <InlineJobsList
          jobs={expandedJobsSection.jobs}
          total={expandedJobsSection.total}
          commentary={expandedJobsSection.commentary}
          loadMore={pagers.loadMoreJobs}
        />
      </div>
    );
  }

  if (contactsSection?.type === 'contactsSection') {
    stageContent.contacts = (
      <div className="px-3 py-3 sm:px-4">
        <InlineContactsList
          contacts={contactsSection.contacts}
          total={contactsSection.total}
          commentary={contactsSection.commentary}
          loadMore={pagers.loadMoreContacts}
        />
      </div>
    );
  }

  if (answerSection?.type === 'answerSection') {
    stageContent.answer = (
      <AnswerCard
        answer={answerSection.answer}
        isStreaming={answerSection.isStreaming}
      />
    );
  }

  const allCompanyNames = useMemo(() => {
    const names: string[] = [];
    if (exactCompanyName) names.push(exactCompanyName);
    names.push(...similarCompanyNames);
    return names;
  }, [exactCompanyName, similarCompanyNames]);

  if (resultFilter === 'contacts' && allCompanyNames.length > 0) {
    stageContent.companies = (
      <div className="px-3 py-3 sm:px-4">
        <CompanyContactsFlat companyNames={allCompanyNames} />
      </div>
    );
  }

  const companyStages: StageKey[] = ['exactMatch', 'similarExpansion', 'companies'];
  let companyLogShown = false;

  const filteredLogs = logs.filter((log) => {
    if (!allowedStages.has(log.stageKey)) return false;
    if (log.stageKey === 'answer' && answerSection) return false;
    if (log.stageKey === 'understanding' && answerSection) return false;
    if (companyStages.includes(log.stageKey)) {
      if (!stageContent[log.stageKey] && !companyLogShown) return false;
      if (companyLogShown) return false;
      companyLogShown = true;
    }
    return true;
  });

  return (
    <>
      <div className="mx-3 my-2 overflow-hidden rounded-lg border border-border/40 bg-card sm:mx-4">
        <QueryMessage query={round.query} resumeTitle={round.resumeTitle} onEdit={onEditQuery} onDelete={onDeleteRound} />
        {answerSection?.type === 'answerSection' && (
          <AnswerCard answer={answerSection.answer} isStreaming={answerSection.isStreaming} />
        )}
      </div>
      {filteredLogs.map((log) => (
        <UnifiedStep
          key={log.id}
          log={log}
          content={stageContent[log.stageKey]}
        />
      ))}
    </>
  );
}

function ActiveLoadingSection({ stages }: { stages: StageState }) {
  const activeMessages = useMemo(() => {
    const msgs: string[] = [];
    if (stages.understanding === 'running') msgs.push(...UNDERSTANDING_MESSAGES);
    if (stages.exactMatch === 'running') msgs.push(...COMPANIES_MESSAGES);
    if (stages.similarExpansion === 'running') msgs.push(...SIMILAR_MESSAGES);
    if (stages.jobs === 'running') msgs.push(...JOBS_MESSAGES);
    if (stages.expandedJobs === 'running') msgs.push(...EXPANDED_JOBS_MESSAGES);
    if (stages.contacts === 'running') msgs.push(...CONTACTS_MESSAGES);
    if (stages.answer === 'running') msgs.push(...ANSWER_MESSAGES);
    return msgs;
  }, [stages.understanding, stages.exactMatch, stages.similarExpansion, stages.jobs, stages.expandedJobs, stages.contacts, stages.answer]);

  if (activeMessages.length === 0) return null;

  return (
    <div className="px-3 py-3 sm:px-4" style={{ overflowAnchor: 'none' }}>
      <InlineLoadingHint messages={activeMessages} />
    </div>
  );
}

/* ----------------------------- Unified step ------------------------------ */

function UnifiedStep({ log, content }: { log: SearchLogEntry; content?: React.ReactNode }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const Icon = STAGE_ICONS[log.stageKey];
  const hasDetails = log.details && log.details.length > 0;

  return (
    <div className="animate-[slideUpFade_0.3s_ease-out]">
      <div className="flex items-start gap-2.5 px-3 pt-2.5 sm:px-4">
        <div className="mt-0.5 text-primary">
          <Icon className="h-3 w-3" />
        </div>
        <div className="min-w-0 flex-1 pb-2.5">
          {hasDetails ? (
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              className="flex w-full items-center gap-1.5 text-left"
            >
              <p className="flex-1 pt-0.5 text-xs font-medium text-foreground/85">
                {log.message}
              </p>
              <ChevronDown
                className={cn(
                  'h-3 w-3 flex-shrink-0 text-muted-foreground transition-transform',
                  detailsOpen && 'rotate-180',
                )}
              />
            </button>
          ) : (
            <p className="pt-0.5 text-xs font-medium text-foreground/85">
              {log.message}
            </p>
          )}
          {detailsOpen && hasDetails && (
            <div className="mt-1.5 space-y-1.5">
              {log.details!.map((detail) => (
                <DetailLine key={detail} text={detail} />
              ))}
            </div>
          )}
        </div>
      </div>
      {content}
    </div>
  );
}

/* -------------------------------- Detail line ------------------------------ */

const DETAIL_LABELS: Record<string, string> = {
  'Similar titles:': 'Recommended roles:',
  'Similar skills:': 'Related skills:',
  'Similar companies:': 'Similar companies:',
};

function DetailLine({ text }: { text: string }) {
  for (const [prefix, label] of Object.entries(DETAIL_LABELS)) {
    if (text.startsWith(prefix)) {
      const values = text.slice(prefix.length).split(',').map((v) => v.trim()).filter(Boolean).slice(0, 5);
      if (values.length === 0) break;
      return <p className="text-[11px] text-muted-foreground">{label} {values.join(', ')}</p>;
    }
  }

  return <p className="text-[11px] text-muted-foreground">{text}</p>;
}

/* -------------------------------- Sub-sections ----------------------------- */

function InlineJobsList({
  jobs,
  total,
  commentary,
  loadMore,
}: {
  jobs: JobWithContacts[];
  total: number;
  commentary?: string;
  loadMore: (offset: number) => Promise<JobWithContacts[]>;
}) {
  const list = useAsyncList<JobWithContacts>({
    initial: jobs,
    total,
    fetcher: loadMore,
  });

  return (
    <div className="space-y-3">
      {commentary ? <FeedInsight text={commentary} placement="between" /> : null}
      <div className="space-y-2">
        {list.items.map((jobWithContacts) => (
          <JobWithContactsCard key={jobWithContacts.job.id} data={jobWithContacts} />
        ))}
      </div>
      {list.hasMore ? (
        <div className="flex justify-center pt-1">
          <ShowMoreButton
            loading={list.loading}
            remaining={Math.min(3, total - list.items.length)}
            noun="jobs"
            onClick={list.loadMore}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Convert ContactEntityData to a Contact for dialogs that require the
 * dashboard Contact type. Uses sourceContact when available.
 */
function toDialogContact(data: ContactEntityData): Contact {
  if (data.sourceContact && 'firstName' in data.sourceContact) {
    return data.sourceContact as Contact;
  }
  const nameParts = data.fullName.split(' ');
  return {
    id: data.id,
    firstName: nameParts[0] ?? '',
    lastName: nameParts.slice(1).join(' ') ?? '',
    fullName: data.fullName,
    title: data.title ?? '',
    company: data.company?.name ?? '',
    companyId: data.company?.id,
    email: data.email,
    emails: data.emails,
    phones: data.phones,
    linkedIn: data.linkedInUrl,
    location: data.location ?? '',
    seniority: data.seniority ?? '',
    relevanceScore: data.relevanceScore ?? 0,
    relevanceReason: data.relevanceReason ?? '',
    playbookId: '',
    discoveredAt: new Date(),
    isFavorite: data.isFavorite ?? false,
    tags: data.tags ?? [],
    hasOutreach: data.hasOutreach ?? false,
    status: data.status,
    spotlight: data.spotlight,
    spotlightScore: data.spotlightScore,
    relevanceLikelihood: data.relevanceLikelihood,
    responseLikelihood: data.responseLikelihood,
    hiringImpactLikelihood: data.hiringImpactLikelihood,
  };
}

function InlineContactsList({
  contacts,
  total,
  commentary,
  loadMore,
}: {
  contacts: ContactEntityData[];
  total: number;
  commentary?: string;
  loadMore: (offset: number) => Promise<ContactEntityData[]>;
}) {
  const router = useRouter();
  const list = useAsyncList<ContactEntityData>({
    initial: contacts,
    total,
    fetcher: loadMore,
  });

  const [outreachContact, setOutreachContact] = useState<Contact | null>(null);
  const [quickViewContact, setQuickViewContact] = useState<Contact | null>(null);

  const navigateToContacts = useCallback(() => {
    router.push('/d/contacts');
  }, [router]);

  return (
    <>
      <div className="space-y-3">
        {commentary ? <FeedInsight text={commentary} placement="between" /> : null}
        <div className="space-y-2">
          {list.items.map((contact) => (
            <ContactEntity
              key={contact.id}
              contact={contact}
              layout="row"
              fields={{ email: true, linkedIn: true, location: true, tags: true, relevance: false, seniority: false, status: true }}
              actions={{ viewProfile: true, reachOut: true }}
              showHiddenFieldHints
              onViewProfile={() => setQuickViewContact(toDialogContact(contact))}
              onReachOut={() => setOutreachContact(toDialogContact(contact))}
              onStatusChange={() => navigateToContacts()}
            />
          ))}
        </div>
        {list.hasMore ? (
          <div className="flex justify-center pt-1">
            <ShowMoreButton
              loading={list.loading}
              remaining={Math.min(3, total - list.items.length)}
              noun="contacts"
              onClick={list.loadMore}
            />
          </div>
        ) : null}
      </div>

      <ConnectedOutreachDialog
        open={outreachContact !== null}
        onOpenChange={(open) => { if (!open) setOutreachContact(null); }}
        contact={outreachContact}
      />
      <ContactQuickViewDialog
        open={quickViewContact !== null}
        onOpenChange={(open) => { if (!open) setQuickViewContact(null); }}
        contact={quickViewContact}
        onReachOut={(contact) => {
          setQuickViewContact(null);
          setOutreachContact(contact);
        }}
      />
    </>
  );
}

/* --------------------------------- Loading --------------------------------- */

const UNDERSTANDING_MESSAGES = [
  'Analyzing your search query…',
  'Understanding what you\'re looking for…',
  'Breaking down your search criteria…',
];

const COMPANIES_MESSAGES = [
  'Finding your best-matching companies…',
  'Ranking by relevance to your search…',
  'Almost there — this is worth the wait',
  'Analyzing company profiles for you…',
];

const SIMILAR_MESSAGES = [
  'Looking for similar companies…',
  'Expanding your search radius…',
  'Finding related companies in the same space…',
  'Almost there — broadening results',
];

const JOBS_MESSAGES = [
  'Scanning open positions for you…',
  'Matching roles to your criteria…',
  'Curating the best opportunities…',
  'Hang tight — great roles incoming',
];

const EXPANDED_JOBS_MESSAGES = [
  'Searching for similar role opportunities…',
  'Expanding to related job titles…',
  'Finding positions for related roles…',
];

const CONTACTS_MESSAGES = [
  'Discovering key people for you…',
  'Finding decision-makers in your area…',
  'Building your contact shortlist…',
  'Almost ready — quality takes a moment',
];

const ANSWER_MESSAGES = ['Thinking about your question…', 'Generating an answer…'];

function InlineLoadingHint({ messages }: { messages: string[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
    const id = setInterval(() => setIndex((i) => (i + 1) % messages.length), 4000);
    return () => clearInterval(id);
  }, [messages]);

  const safeIndex = index % messages.length;

  return (
    <div className="flex items-center gap-3">
      <ArrowPathIcon className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-muted-foreground" />
      <p
        key={safeIndex}
        className="text-xs text-muted-foreground animate-[slideUpFade_0.5s_ease-out]"
      >
        {messages[safeIndex]}
      </p>
    </div>
  );
}
